import { Link, useNavigate } from 'react-router-dom';
import { LogIn, LogOut, User } from 'lucide-react';
import { useAuth } from '@kss/auth';

/**
 * Sticky landing header.
 *
 * The "Sign in" affordance is now a user-icon button that pops the
 * global LoginModal (via the `kss:open-login` custom event) instead of
 * navigating to a separate page. When the visitor is already signed in
 * the icon flips to a "Go to panel" + sign-out cluster.
 */
export default function TopNav() {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();

  function openLogin(tab: 'signin' | 'signup' | 'forgot' = 'signin') {
    window.dispatchEvent(
      new CustomEvent('kss:open-login', { detail: { tab, redirect: '/panel/dashboard' } }),
    );
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b border-[var(--color-border)] bg-[var(--color-bg-card)]/80 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link to="/" className="text-lg font-semibold tracking-tight">
          KSS
        </Link>

        <nav className="hidden items-center gap-6 text-sm sm:flex">
          <a href="#why-us" className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]">Why us</a>
          <a href="#pricing" className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]">Pricing</a>
          <a href="#faq" className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]">FAQ</a>
          <Link to="/portal" className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]">Customer portal</Link>
        </nav>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/panel/dashboard')}
                className="hidden h-9 items-center gap-2 rounded-md border border-[var(--color-border)] px-3 text-sm sm:flex"
              >
                <User size={14} />
                <span className="max-w-[120px] truncate">{user?.name ?? 'Account'}</span>
              </button>
              <button
                type="button"
                onClick={() => logout()}
                title="Sign out"
                aria-label="Sign out"
                className="grid h-9 w-9 place-items-center rounded-full border border-[var(--color-border)] hover:border-[var(--color-primary)]"
              >
                <LogOut size={14} />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="hidden h-9 items-center rounded-md border border-[var(--color-border)] px-3 text-sm sm:inline-flex"
              >
                Sign up
              </button>
              <button
                type="button"
                onClick={() => openLogin('signin')}
                title="Sign in"
                aria-label="Sign in"
                className="group grid h-9 w-9 place-items-center rounded-full border border-[var(--color-border)] bg-[var(--color-bg-card)] transition-colors hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white"
              >
                <User size={16} className="group-hover:hidden" />
                <LogIn size={16} className="hidden group-hover:block" />
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
